import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { applyUserLanguage } from '../i18n';
import {
  ResponseArtifactDTO,
  SubscriptionPlanOption,
  PublicSubscriptionCheckoutResponse,
  PublicCreditTopUpRequest,
  PublicCreditTopUpResponse,
  TopUpProduct,
} from '@dreamai/types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import api from '../api';
import ResponseArtifactModal from './chat/sidebar/ResponseArtifactModal';
import { isOrganisationChargeError } from './chat/sidebar/organisationSpend';
import { hasSecureRequestId } from './chat/sidebar/secureInputArtifact';
import { getPurchaseApproval } from './chat/sidebar/purchaseApprovalArtifact';
import {
  isHotelQuoteExpiredError,
  isHotelsView,
} from './chat/sidebar/hotelArtifact';
import ExpenseReviewModal from './chat/sidebar/ExpenseReviewModal';
import { isExpenseReviewArtifact } from './chat/sidebar/expenseReviewArtifact';
import { isFlightsView } from './chat/sidebar/flightArtifact';
import { areDreamRailChoices } from './chat/sidebar/railArtifact';
import TransactionalSheet from './chat/sidebar/TransactionalSheet';
import SecureInputStage, {
  SecureInputStageKind,
} from './chat/sidebar/SecureInputStage';
import TopUpModal from './settings/TopUpModal';
import type { CardSelection } from './chat/sidebar/SpendingApprovalModal';
import { ProjectAppRenderer, SharedAppData } from './SharedProjectAppPage';
import ProofPage, { ProofPageData } from './ProofPage';
import { ReplyToProvider } from '../contexts/ReplyToContext';
import { AgentProvider } from '../contexts/AgentContext';
import { MobileNavProvider } from '../contexts/MobileNavContext';
import { OnboardingModalsProvider } from '../providers/OnboardingModalsProvider';
import { HotelBookingProgressProvider } from '../providers/HotelBookingProgressProvider';
import { FlightBookingProgressProvider } from '../providers/FlightBookingProgressProvider';
import { PLAN_TAGLINES, getFeatureList } from './chat/sidebar/planFeatures';
import PublicNoticePage from './common/PublicNoticePage';
import {
  isLivePricesRequired,
  needsLivePrices,
  refreshLivePrices,
} from './sharedArtifactLivePrices';
import {
  PAYMENT_PRIVATE_ONLY_MESSAGE,
  isPrivateOnlyPaymentArtifact,
} from './chat/sidebar/purchaseApprovalArtifact';
import {
  isSettledBookingState,
  isSystemBookingArtifact,
  spendApprovalBookingState,
} from './chat/sidebar/spendApprovalBooking';
import './shared-artifact-page.scss';
import './chat/sidebar/plan-selection-modal.scss';

const isSecureLoginArtifact = (art: ResponseArtifactDTO): boolean =>
  art.artifactType === 'form_input' &&
  !!art.isSecureCredential &&
  art.credentialKind !== 'card';

const isSecureCardArtifact = (art: ResponseArtifactDTO): boolean =>
  art.artifactType === 'form_input' &&
  !!art.isSecureCredential &&
  art.credentialKind === 'card';

// Terminal screen for the secure (vaulted) flows: the same sheet the
// sign-in / card form rendered in, so the page doesn't change language at
// the last step.
const SecureTerminal: React.FC<{
  kind: SecureInputStageKind;
  title: React.ReactNode;
  body: string;
}> = ({ kind, title, body }) => (
  <TransactionalSheet
    show
    presentation='standalone'
    className='secure-input-sheet'
    ariaLabelledBy='secure-input-title'
  >
    <SecureInputStage
      key={kind}
      kind={kind}
      title={title}
      titleId='secure-input-title'
    >
      <p>{body}</p>
    </SecureInputStage>
  </TransactionalSheet>
);

const CheckIcon: React.FC = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='#2298dd'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M5 13l4 4L19 7' />
  </svg>
);

const ArrowRightIcon: React.FC = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <line x1='4' y1='12' x2='20' y2='12' />
    <polyline points='14 6 20 12 14 18' />
  </svg>
);

/**
 * Public page for viewing and responding to a response artifact via share link.
 * Rendered at /artifact/:shareToken — no authentication required.
 * Used for WhatsApp users to interact with artifacts in full-screen.
 */
const SharedArtifactPage: React.FC = () => {
  const { t } = useTranslation();
  const { shareToken } = useParams<{ shareToken: string }>();
  const [searchParams] = useSearchParams();
  const [artifact, setArtifact] = useState<ResponseArtifactDTO | null>(null);
  const [projectApp, setProjectApp] = useState<SharedAppData | null>(null);
  const [proofPage, setProofPage] = useState<ProofPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [gettingLivePrices, setGettingLivePrices] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const pendingActionedRef = useRef(false);
  const pendingCancelledRef = useRef(false);
  const [secureCancelled, setSecureCancelled] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [activePlanIdx, setActivePlanIdx] = useState(0);
  const [topUpSubmitting, setTopUpSubmitting] = useState(false);
  const [topUpError, setTopUpError] = useState<string | null>(null);
  const [topUpCreditsAdded, setTopUpCreditsAdded] = useState<number | null>(
    null,
  );
  const [agentWhatsappNumber, setAgentWhatsappNumber] = useState<
    string | undefined
  >(undefined);
  const [agentName, setAgentName] = useState<string | undefined>(undefined);

  // Approvals the system books itself: after the user approves, follow the
  // booking on the artifact until it settles so the page can say what
  // happened rather than just "response submitted".
  const bookingState = spendApprovalBookingState(artifact);
  const followBooking =
    !!shareToken &&
    isSystemBookingArtifact(artifact) &&
    (submitted || artifact.status === 'actioned') &&
    artifact.userResponse?.approved !== false &&
    !isSettledBookingState(bookingState);
  useEffect(() => {
    if (!followBooking) return;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const response = await api.get<{
          success: boolean;
          artifact: ResponseArtifactDTO;
        }>(`/public/artifacts/${shareToken}`);
        if (cancelled) return;
        if (response.data.success && response.data.artifact) {
          setArtifact((prev) =>
            prev
              ? {
                  ...prev,
                  status: response.data.artifact.status,
                  userResponse: response.data.artifact.userResponse,
                }
              : response.data.artifact,
          );
        }
      } catch {
        // transient — try again on the next tick
      }
    };
    const interval = window.setInterval(() => {
      if (attempts >= 60) {
        window.clearInterval(interval);
        return;
      }
      void tick();
    }, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [followBooking, shareToken]);

  useEffect(() => {
    const fetchArtifact = async () => {
      if (!shareToken) {
        setError(t('artifact.noShareToken'));
        setLoading(false);
        return;
      }

      const load = () =>
        api.get<{
          success: boolean;
          artifact: ResponseArtifactDTO;
          projectApp?: SharedAppData;
          proofPage?: ProofPageData;
          agentWhatsappNumber?: string;
          agentName?: string;
          language?: string | null;
          error?: string;
        }>(`/public/artifacts/${shareToken}`);
      const refreshAndLoad = async () => {
        setGettingLivePrices(true);
        await refreshLivePrices(shareToken);
        return load();
      };

      try {
        let response = await load().catch((err) => {
          if (isLivePricesRequired(err)) return refreshAndLoad();
          throw err;
        });
        if (
          response.data.success &&
          response.data.artifact &&
          needsLivePrices(response.data.artifact)
        ) {
          response = await refreshAndLoad().catch(() => response);
        }

        if (response.data.success && response.data.artifact) {
          applyUserLanguage(response.data.language);
          // Payment code forms and the live bank challenge are private to
          // the owner's own Dream chat; only the purchase approval card may
          // open from a share link (it actions through its own share-token
          // routes, never the PATCH below).
          if (isPrivateOnlyPaymentArtifact(response.data.artifact)) {
            setError(PAYMENT_PRIVATE_ONLY_MESSAGE);
            return;
          }
          setArtifact(response.data.artifact);
          if (response.data.projectApp) {
            setProjectApp(response.data.projectApp);
          }
          if (response.data.proofPage) {
            setProofPage(response.data.proofPage);
          }
          setAgentWhatsappNumber(response.data.agentWhatsappNumber);
          setAgentName(response.data.agentName);
        } else {
          setError(response.data.error || t('artifact.loadFailed'));
        }
      } catch (err: any) {
        if (err.response?.status === 410) {
          setError(t('artifact.linkExpired'));
        } else if (err.response?.status === 404) {
          setError(t('artifact.linkInvalid'));
        } else {
          setError(t('artifact.genericError'));
        }
      } finally {
        setGettingLivePrices(false);
        setLoading(false);
      }
    };

    fetchArtifact();
  }, [shareToken]);

  const handleSubmitResponse = useCallback(
    async (
      art: ResponseArtifactDTO,
      response: any,
      mandateOptions?: {
        createMandate: boolean;
        mandateItems: Array<{
          actionLine: string;
          descriptionLine: string;
          price?: number;
          price_currency?: string;
          isDeposit?: boolean;
        }>;
      },
      cardSelection?: CardSelection,
    ) => {
      if (!shareToken || !artifact) return;

      try {
        await api.patch(`/response-artifacts/${art.id}`, {
          artifactId: art.id,
          status: 'actioned',
          userResponse: response,
          shareToken,
          ...(mandateOptions?.createMandate && {
            create_mandate: true,
            mandate_items: mandateOptions.mandateItems,
          }),
          ...(cardSelection && { card_selection: cardSelection }),
        });
        // The secure sign-in sheet renders its own "In the vault" screen and
        // tells us via onClose when the user has read it. Flipping to the
        // page-level success here would unmount it mid-flow.
        if (isSecureLoginArtifact(art)) {
          pendingActionedRef.current = true;
          return;
        }
        // A hotel / flight Book press is booked by the system after this
        // request; the view keeps its confirm sheet open to show progress and
        // the outcome, so leave it mounted and read-only rather than swapping
        // in the page-level success.
        if (
          (isHotelsView(art.view) || isFlightsView(art.view)) &&
          response?.selectedOfferId
        ) {
          setArtifact((prev) =>
            prev
              ? { ...prev, status: 'actioned', userResponse: response }
              : prev,
          );
          return;
        }
        setArtifact((prev) =>
          prev ? { ...prev, status: 'actioned', userResponse: response } : prev,
        );
        setSubmitted(true);
      } catch (err: any) {
        // The sign-in sheet renders failures inline (with retry), so hand
        // the error back rather than replacing the page with an error card.
        if (isSecureLoginArtifact(art)) throw err;
        if (isOrganisationChargeError(err)) throw err;
        // The hotel sheet re-quotes and stays open; nothing was approved.
        if (isHotelQuoteExpiredError(err)) throw err;
        if (err.response?.data?.code === 'PAYMENT_METHOD_UNAVAILABLE') {
          setError(
            err.response?.data?.error || "This payment method isn't available.",
          );
        } else if (
          err.response?.status === 410 &&
          err.response?.data?.code === 'PAYMENT_SESSION_EXPIRED'
        ) {
          setError(t('artifact.sessionExpired'));
        } else if (err.response?.status === 410) {
          setError(t('artifact.linkExpiredShort'));
        } else if (err.response?.status === 409) {
          setError(t('artifact.alreadyResponded'));
        } else {
          setError(t('artifact.submitFailed'));
        }
      }
    },
    [shareToken, artifact],
  );

  // The secure sign-in form and the purchase approval card never PATCH the
  // artifact: they post straight to their share-token endpoints and report
  // success here, so the page flips to its success card on close.
  const handleOptimisticAction = useCallback(
    (art: ResponseArtifactDTO, action: 'submit' | 'dismiss') => {
      if (!hasSecureRequestId(art) && !getPurchaseApproval(art)) return;
      if (action === 'submit') pendingActionedRef.current = true;
      else pendingCancelledRef.current = true;
    },
    [],
  );

  const handleModalClose = useCallback(() => {
    if (pendingCancelledRef.current) {
      pendingCancelledRef.current = false;
      setSecureCancelled(true);
      return;
    }
    if (!pendingActionedRef.current) return;
    pendingActionedRef.current = false;
    setArtifact((prev) => (prev ? { ...prev, status: 'actioned' } : prev));
    setSubmitted(true);
  }, []);

  if (loading) {
    return (
      <PublicNoticePage
        loading
        loadingLabel={
          gettingLivePrices ? t('artifact.gettingLivePrices') : undefined
        }
      />
    );
  }

  if (error) {
    return <PublicNoticePage title={t('artifact.unableToLoad')} body={error} />;
  }

  if (submitted && artifact && isSecureLoginArtifact(artifact)) {
    return (
      <SecureTerminal
        kind='signed-in'
        title={
          <>
            {t('artifact.secureLoginDone')}{' '}
            <span className='accent'>
              {t('artifact.secureLoginDoneAccent')}
            </span>
          </>
        }
        body={t('artifact.secureLoginDoneBody')}
      />
    );
  }

  if (submitted && artifact && isSecureCardArtifact(artifact)) {
    return (
      <SecureTerminal
        kind='signed-in'
        title={
          <>
            {t('artifact.secureCardSaved')}{' '}
            <span className='accent'>
              {t('artifact.secureCardSavedAccent')}
            </span>
          </>
        }
        body={t('artifact.secureCardSavedBody')}
      />
    );
  }

  if (
    artifact &&
    isSystemBookingArtifact(artifact) &&
    (submitted || artifact.status === 'actioned') &&
    artifact.userResponse?.approved !== false
  ) {
    switch (bookingState?.state) {
      case 'booked':
        return (
          <PublicNoticePage
            tone='success'
            title={t('artifact.booked')}
            body={
              <>
                {bookingState.summary ?? t('artifact.bookedBody')}
                {bookingState.reference && (
                  <>
                    <br />
                    {t('artifact.bookedReference', {
                      reference: bookingState.reference,
                    })}
                  </>
                )}
              </>
            }
            testId='system-booking-booked'
          />
        );
      case 'not_booked':
        return (
          <PublicNoticePage
            title={t('artifact.notBooked')}
            body={bookingState.message ?? t('artifact.notBookedBody')}
            testId='system-booking-not-booked'
          />
        );
      case 'unknown':
        return (
          <PublicNoticePage
            title={t('artifact.bookingUnknown')}
            body={t('artifact.bookingUnknownBody')}
            testId='system-booking-unknown'
          />
        );
      default:
        return (
          <PublicNoticePage
            title={t('artifact.bookingNow')}
            body={t('artifact.bookingNowBody')}
            testId='system-booking-in-progress'
          />
        );
    }
  }

  if (submitted) {
    return (
      <PublicNoticePage
        tone='success'
        title={t('artifact.responseSubmitted')}
        body={t('artifact.responseSubmittedBody')}
      />
    );
  }

  if (!artifact) return null;

  const isActioned = artifact.status === 'actioned';
  const isActionedRailList =
    isActioned &&
    Array.isArray(artifact.choices) &&
    areDreamRailChoices(
      artifact.choices,
      artifact.choices.map((choice) => choice.choiceId),
    );

  // Credit top-up artifacts render as a login-free version of the Settings
  // top-up modal: quantity stepper, card on file, optional auto top-up.
  if (artifact.artifactType === 'credit_topup') {
    const topUpOption = artifact.topUpOption;
    const topUpPending = searchParams.get('status') === 'topup_pending';

    if (topUpPending && !isActioned) {
      return (
        <PublicNoticePage
          tone='success'
          title={t('artifact.paymentReceived')}
          body={t('artifact.paymentReceivedBody')}
        />
      );
    }

    if (isActioned || topUpCreditsAdded !== null) {
      return (
        <PublicNoticePage
          tone='success'
          title={t('artifact.topUpComplete')}
          body={
            topUpCreditsAdded !== null
              ? t('artifact.topUpCreditsAdded', {
                  credits: topUpCreditsAdded.toLocaleString('en-GB'),
                })
              : t('artifact.topUpAlreadyDone')
          }
        />
      );
    }

    if (!topUpOption) {
      return (
        <PublicNoticePage
          title={t('artifact.unableToLoad')}
          body={t('artifact.topUpMissingDetails')}
        />
      );
    }

    const product: TopUpProduct = {
      priceId: topUpOption.priceId,
      creditType: 'credit',
      creditsPerUnit: topUpOption.creditsPerUnit,
      unitAmount: topUpOption.unitAmount,
      currency: topUpOption.currency,
    };

    const handleTopUpConfirm = async (
      topUp: TopUpProduct,
      quantity: number,
      autoTopupCap: number | null,
    ) => {
      if (!shareToken || topUpSubmitting) return;
      setTopUpSubmitting(true);
      setTopUpError(null);

      // No card on file: first-time top-up goes through the Airwallex hosted
      // checkout, which charges the selected amount and saves the card.
      if (!artifact.cardInfo) {
        try {
          const response = await api.post<PublicSubscriptionCheckoutResponse>(
            `/public/artifacts/${shareToken}/topup-checkout`,
            { priceId: topUp.priceId, quantity },
          );
          if (response.data.success && response.data.checkoutUrl) {
            window.location.href = response.data.checkoutUrl;
            return;
          }
          setTopUpError(response.data.error || t('artifact.checkoutFailed'));
        } catch (err: any) {
          if (err.response?.status === 410) {
            setTopUpError(t('artifact.linkExpiredAskDream'));
          } else if (err.response?.status === 409) {
            setTopUpError(t('artifact.topUpAlreadyCompleted'));
          } else {
            setTopUpError(
              err.response?.data?.error || t('artifact.checkoutFailed'),
            );
          }
        }
        setTopUpSubmitting(false);
        return;
      }

      try {
        const body: PublicCreditTopUpRequest = {
          priceId: topUp.priceId,
          quantity,
          ...(autoTopupCap !== null
            ? { autoTopup: { enable: true, monthlyLimit: autoTopupCap } }
            : {}),
        };
        const response = await api.post<PublicCreditTopUpResponse>(
          `/public/artifacts/${shareToken}/credit-topup`,
          body,
        );
        if (response.data.success) {
          setTopUpCreditsAdded(response.data.creditsAdded ?? 0);
          return;
        }
        setTopUpError(response.data.error || t('artifact.topUpFailed'));
      } catch (err: any) {
        if (err.response?.status === 410) {
          setTopUpError(t('artifact.linkExpiredAskDream'));
        } else if (err.response?.status === 409) {
          setTopUpError(t('artifact.topUpAlreadyCompleted'));
        } else {
          setTopUpError(err.response?.data?.error || t('artifact.topUpFailed'));
        }
      }
      setTopUpSubmitting(false);
    };

    return (
      <div className='shared-topup-page'>
        <TopUpModal
          isOpen={true}
          onClose={() => {}}
          onConfirm={handleTopUpConfirm}
          topUp={product}
          cardInfo={artifact.cardInfo ?? null}
          isSubmitting={topUpSubmitting}
          error={topUpError}
          offerAutoTopup={!!artifact.cardInfo}
          initialAutoTopup={artifact.autoTopup}
          hideClose={true}
          confirmLabel={
            artifact.cardInfo ? undefined : t('artifact.continueSecureCheckout')
          }
        />
      </div>
    );
  }

  // Plan selection artifacts render as a login-free plan chooser; checkout
  // happens on the Airwallex hosted payment page.
  if (artifact.artifactType === 'plan_selection') {
    const plans: SubscriptionPlanOption[] = artifact.subscriptionPlans ?? [];
    const subscriptionPending =
      searchParams.get('status') === 'subscription_pending';
    const cardAdded = searchParams.get('status') === 'card_added';

    // Save a card without picking a plan — hosted SETUP checkout, nothing is
    // charged. Lets the assistant top up the purchase-credit card later.
    const handleAddCardOnly = async () => {
      if (!shareToken || checkoutLoading) return;
      setCheckoutError(null);
      setCheckoutLoading('setup-card');
      try {
        const response = await api.post<PublicSubscriptionCheckoutResponse>(
          `/public/artifacts/${shareToken}/setup-payment-method`,
          {},
        );
        if (response.data.success && response.data.checkoutUrl) {
          window.location.href = response.data.checkoutUrl;
          return;
        }
        setCheckoutError(response.data.error || t('artifact.cardSetupFailed'));
      } catch (err: any) {
        if (err.response?.status === 410) {
          setCheckoutError(t('artifact.linkExpiredAskDream'));
        } else {
          setCheckoutError(
            err.response?.data?.error || t('artifact.cardSetupFailed'),
          );
        }
      }
      setCheckoutLoading(null);
    };

    const handleSelectPlan = async (plan: SubscriptionPlanOption) => {
      if (!shareToken || checkoutLoading || plan.isCurrentPlan) return;
      setCheckoutError(null);
      setCheckoutLoading(plan.priceId);
      try {
        const response = await api.post<PublicSubscriptionCheckoutResponse>(
          `/public/artifacts/${shareToken}/subscription-checkout`,
          { priceId: plan.priceId },
        );
        if (response.data.success && response.data.checkoutUrl) {
          window.location.href = response.data.checkoutUrl;
          return;
        }
        setCheckoutError(response.data.error || t('artifact.checkoutFailed'));
      } catch (err: any) {
        if (err.response?.status === 410) {
          setCheckoutError(t('artifact.linkExpiredAskDream'));
        } else {
          setCheckoutError(
            err.response?.data?.error || t('artifact.checkoutFailed'),
          );
        }
      }
      setCheckoutLoading(null);
    };

    if (cardAdded) {
      return (
        <PublicNoticePage
          tone='success'
          title={t('artifact.cardAdded')}
          body={t('artifact.cardAddedBody')}
        />
      );
    }

    if (isActioned || subscriptionPending) {
      return (
        <PublicNoticePage
          tone='success'
          title={
            isActioned
              ? t('artifact.subscriptionActive')
              : t('artifact.paymentReceived')
          }
          body={
            isActioned
              ? t('artifact.subscriptionActiveBody')
              : t('artifact.subscriptionPendingBody')
          }
        />
      );
    }

    return (
      <div className='plan-selection-modal-overlay shared-plan-selection'>
        <div className='plan-selection-modal'>
          <div className='modal-header'>
            <h2>{t('artifact.selectPlan')}</h2>
          </div>

          <div className='modal-body'>
            <div className='plan-tabs'>
              {plans.map((plan, i) => (
                <button
                  key={plan.priceId}
                  className={`plan-tab${i === activePlanIdx ? ' selected' : ''}`}
                  onClick={() => setActivePlanIdx(i)}
                >
                  {plan.name}
                </button>
              ))}
            </div>
            <div className='plans-grid'>
              {plans.map((plan, planIndex) => {
                const { heading, features } = getFeatureList(plan.name);
                const isPopular = plan.name.toLowerCase() === 'pro';
                const isCurrent = !!plan.isCurrentPlan;
                const isSubmitting = checkoutLoading === plan.priceId;

                return (
                  <div
                    key={plan.priceId}
                    className={`plan-card${isPopular ? ' popular' : ''}${isCurrent ? ' current' : ''}${planIndex === activePlanIdx ? ' mobile-active' : ''}`}
                  >
                    <div className='plan-card-header'>
                      {isPopular && (
                        <span className='popular-badge'>
                          {t('artifact.popular')}
                        </span>
                      )}
                      <div className='plan-name-block'>
                        <h3 className='plan-name'>{plan.name}</h3>
                        <p className='plan-tagline'>
                          {PLAN_TAGLINES[plan.name.toLowerCase()] || ''}
                        </p>
                      </div>
                      <p className='plan-price'>
                        <span className='price-amount'>
                          {plan.displayPrice}
                        </span>
                        <span className='price-period'>
                          /{(plan.periodUnit || 'month').toLowerCase()}
                        </span>
                      </p>
                      <button
                        className={`choose-plan-button${isCurrent ? ' current-plan' : ''}`}
                        onClick={
                          isCurrent ? undefined : () => handleSelectPlan(plan)
                        }
                        disabled={isCurrent || !!checkoutLoading}
                      >
                        {isCurrent ? (
                          t('artifact.yourPlan')
                        ) : isSubmitting ? (
                          t('artifact.loadingShort')
                        ) : (
                          <>
                            {t('artifact.choosePlan', { name: plan.name })}
                            <ArrowRightIcon />
                          </>
                        )}
                      </button>
                    </div>

                    <div className='plan-features'>
                      <p className='features-heading'>{heading}</p>
                      {features.map((feature, i) => (
                        <div key={i} className='feature-row'>
                          <CheckIcon />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            {checkoutError && <p className='plan-error'>{checkoutError}</p>}
            <button
              type='button'
              className='plan-add-card-only'
              onClick={handleAddCardOnly}
              disabled={!!checkoutLoading}
            >
              {checkoutLoading === 'setup-card'
                ? t('artifact.loadingShort')
                : t('artifact.addCardOnly')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Information dump artifacts render as a full-page document (same as /shared/ pages)
  if (artifact.artifactType === 'information_dump') {
    if (proofPage) {
      return <ProofPage proof={proofPage} />;
    }
    if (projectApp && projectApp.blocks.length > 0) {
      return <ProjectAppRenderer appData={projectApp} />;
    }

    // Fallback: legacy markdown content
    const content = (artifact as any).content || artifact.overview;
    return (
      <div className='shared-artifact-page'>
        <div className='shared-artifact-document'>
          {artifact.title && <h1>{artifact.title}</h1>}
          {content ? (
            <div className='document-content'>
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                {content}
              </ReactMarkdown>
            </div>
          ) : (
            <p className='no-content'>{t('artifact.noContent')}</p>
          )}
        </div>
      </div>
    );
  }

  if (secureCancelled) {
    return (
      <SecureTerminal
        kind='cancelled'
        title={
          <>
            {t('artifact.secureLoginCancelledLead')}{' '}
            <span className='accent'>
              {t('artifact.secureLoginCancelledAccent')}
            </span>
          </>
        }
        body={t('artifact.secureLoginCancelledBody')}
      />
    );
  }

  // The WhatsApp "Review" link: the coded expenses waiting to be filed, still
  // readable after Submit so the user can see what went to their accounts.
  if (isExpenseReviewArtifact(artifact)) {
    return (
      <div className='shared-artifact-page'>
        <div className='shared-artifact-container'>
          <ExpenseReviewModal
            show
            artifact={artifact}
            presentation='standalone'
            shareToken={shareToken}
            readOnly={isActioned}
            onSubmit={(response) => handleSubmitResponse(artifact, response)}
          />
        </div>
      </div>
    );
  }

  // Interactive artifacts (yes/no, forms, choices) that have already been
  // responded to have no live controls — show an explicit "already handled"
  // state instead of a dead read-only form.
  if (
    isActioned &&
    !isActionedRailList &&
    !isHotelsView(artifact.view) &&
    !isFlightsView(artifact.view)
  ) {
    return (
      <PublicNoticePage
        tone='success'
        title={t('artifact.alreadyHandled')}
        body={
          artifact.title
            ? t('artifact.alreadyHandledTitled', { title: artifact.title })
            : t('artifact.alreadyHandledUntitled')
        }
      />
    );
  }

  return (
    <div className='shared-artifact-page'>
      <div className='shared-artifact-container'>
        <MobileNavProvider>
          <AgentProvider>
            <OnboardingModalsProvider>
              <ReplyToProvider>
                <HotelBookingProgressProvider>
                  <FlightBookingProgressProvider>
                    <ResponseArtifactModal
                      show={true}
                      onClose={handleModalClose}
                      artifacts={[artifact]}
                      initialIndex={0}
                      onSubmitResponse={handleSubmitResponse}
                      onOptimisticAction={handleOptimisticAction}
                      readOnly={isActioned}
                      shareToken={shareToken}
                      agentWhatsappNumber={agentWhatsappNumber}
                      agentName={agentName}
                    />
                  </FlightBookingProgressProvider>
                </HotelBookingProgressProvider>
              </ReplyToProvider>
            </OnboardingModalsProvider>
          </AgentProvider>
        </MobileNavProvider>
      </div>
    </div>
  );
};

export default SharedArtifactPage;
